"use client";

import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

type ModalOverlayProps = {
    children: ReactNode;
    className?: string;
    panelClassName?: string;
};

function getPortalRoot() {
    return document.documentElement;
}

export function ModalOverlay({ children, className, panelClassName }: ModalOverlayProps) {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!mounted) {
            return;
        }

        const root = document.documentElement;
        const previousHtmlOverflow = root.style.overflow;
        const previousBodyOverflow = document.body.style.overflow;

        root.style.overflow = "hidden";
        document.body.style.overflow = "hidden";

        return () => {
            root.style.overflow = previousHtmlOverflow;
            document.body.style.overflow = previousBodyOverflow;
        };
    }, [mounted]);

    if (!mounted) {
        return null;
    }

    return createPortal(
        <div className={cn("fixed inset-0 z-10001", className)} role="presentation">
            <div className="fixed inset-0 h-dvh w-full bg-slate-950/75 backdrop-blur-md" aria-hidden="true" />
            <div className="fixed left-1/2 top-[50dvh] z-1 w-full max-w-xl -translate-x-1/2 -translate-y-1/2 px-4">
                <div className={cn("max-h-[calc(100dvh-2rem)] overflow-y-auto", panelClassName)}>{children}</div>
            </div>
        </div>,
        getPortalRoot(),
    );
}
